import {
  AppModal,
  Badge,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  FormSubmitButton,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  Typography,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useUpdateMassTortMatterCustomFields } from "@/lib/data/react-query/mass-torts/mass-tort";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type Matter, type MatterMassTortMatterCustomField } from "@/lib/types";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, Pencil } from "lucide-react";
import { orderBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

type MatterMassTortCustomFieldsProps = {
  matter: Matter;
};

const matterCustomFieldsFormSchema = z.object({
  customFields: z.array(
    z.object({
      value: z.string().trim().min(1, "Value is required"),
    }),
  ),
});

type MatterCustomFieldsFormValues = z.infer<
  typeof matterCustomFieldsFormSchema
>;

const FORM_ID = "matter-mass-tort-custom-fields-form";

const getSortedCustomFields = (
  customFields: MatterMassTortMatterCustomField[],
) =>
  orderBy(
    customFields,
    [(customField) => customField.customField?.order],
    ["asc"],
  );

const getFormValues = (
  customFields: MatterMassTortMatterCustomField[],
): MatterCustomFieldsFormValues => ({
  customFields: customFields.map((customField) => ({
    value: customField.value ?? "",
  })),
});

const getSubmittedValue = (
  customField: MatterMassTortMatterCustomField,
  value: string,
) => {
  if (
    customField.customField?.valueType === "text" ||
    customField.customField?.valueType === "date"
  ) {
    return value.trim();
  }

  return value;
};

export function MatterMassTortCustomFields({
  matter,
}: MatterMassTortCustomFieldsProps) {
  const { hasActionPermission } = useUser();
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.massTortMatterCustomField,
    ACTION_PERMISSION_VALUES.update,
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const customFields = useMemo(
    () => getSortedCustomFields(matter.massTortCustomFields),
    [matter.massTortCustomFields],
  );
  const completedCustomFieldsCount = customFields.filter((customField) =>
    Boolean(customField.value),
  ).length;

  if (customFields.length === 0) {
    return (
      <div className="flex flex-col gap-y-4 p-6">
        <Text variant="title">Custom fields</Text>
        <Text variant="subHeading">
          This matter does not have mass tort custom fields.
        </Text>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col gap-5 p-4 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Text variant="title">Custom fields ({customFields.length})</Text>
        <Badge variant="secondary" className="w-fit gap-1.5 px-3 py-1">
          <CheckCircle2 className="h-3.5 w-3.5" />
          {completedCustomFieldsCount} of {customFields.length} completed
        </Badge>
      </div>

      <div className="rounded-md border">
        <div className="border-border bg-muted/30 flex min-w-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <Text variant="subHeading" className="text-foreground">
              Mass tort intake details
            </Text>
            <Text variant="body" className="text-muted-foreground">
              Review the applicable value for each field below.
            </Text>
          </div>
          {canUpdate ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground h-8 w-8 shrink-0"
              onClick={() => {
                setEditModalOpen(true);
              }}
              aria-label="Edit mass tort intake details"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          ) : null}
        </div>

        <div className="flex items-center justify-between border-b px-4 py-2 text-sm font-medium">
          <span>Custom fields</span>
          <span className="text-muted-foreground">
            {customFields.length}{" "}
            {customFields.length === 1 ? "custom field" : "custom fields"}
          </span>
        </div>

        <div className="relative divide-y">
          {customFields.map((customField, index) => {
            const options = customField.customField?.valueOptions ?? [];
            const customFieldId = customField.id;

            return (
              <MatterCustomFieldListItem
                key={customFieldId || `custom-field-${index}`}
                customField={customField}
                index={index}
                options={options}
              />
            );
          })}
        </div>
      </div>

      <MatterMassTortCustomFieldsModal
        open={editModalOpen}
        setOpen={setEditModalOpen}
        matter={matter}
        customFields={customFields}
      />
    </div>
  );
}

type MatterCustomFieldListItemProps = {
  customField: MatterMassTortMatterCustomField;
  index: number;
  options: string[];
};

function MatterCustomFieldListItem({
  customField,
  index,
  options,
}: MatterCustomFieldListItemProps) {
  const hasSelectedValue = Boolean(customField.value);
  const isSelectField = customField.customField?.valueType === "select";

  return (
    <div className="bg-card hover:bg-primary/5 flex flex-col gap-3 px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-muted-foreground w-5 text-sm font-semibold">
          {index + 1}
        </div>
        <div className="min-w-0 flex-1">
          <Typography weight="medium" className="truncate">
            {customField.customField?.label || "Custom Field"}
          </Typography>
        </div>
        <Badge
          variant={hasSelectedValue ? "outline" : "secondary"}
          className="shrink-0"
        >
          {hasSelectedValue ? "Completed" : "Needs value"}
        </Badge>
      </div>

      <div className="ml-8 flex flex-col gap-3">
        <div className="text-muted-foreground min-w-0 text-xs">
          <span className="text-foreground font-medium">Description: </span>
          {customField.customField?.description || "-"}
          {isSelectField && options.length === 0 ? (
            <Text variant="subHeading" className="mt-2">
              No options have been configured for this field.
            </Text>
          ) : null}
        </div>

        <div className="max-w-md">
          <Text variant="body" className="text-foreground font-medium">
            {customField.value || "-"}
          </Text>
        </div>
      </div>
    </div>
  );
}

type MatterMassTortCustomFieldsModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  matter: Matter;
  customFields: MatterMassTortMatterCustomField[];
};

function MatterMassTortCustomFieldsModal({
  open,
  setOpen,
  matter,
  customFields,
}: MatterMassTortCustomFieldsModalProps) {
  const updateCustomFields = useUpdateMassTortMatterCustomFields({
    successToast: null,
  });
  const form = useForm<MatterCustomFieldsFormValues>({
    resolver: zodResolver(matterCustomFieldsFormSchema),
    defaultValues: getFormValues(customFields),
  });
  const isFormLoading = updateCustomFields.isPending;

  useEffect(() => {
    if (open) {
      form.reset(getFormValues(customFields));
    }
  }, [customFields, form, open]);

  const onSubmit = async (data: MatterCustomFieldsFormValues) => {
    const changedFields = customFields.flatMap((customField, index) => {
      const nextValue = getSubmittedValue(
        customField,
        data.customFields[index]?.value ?? "",
      );
      const currentValue = getSubmittedValue(
        customField,
        customField.value ?? "",
      );

      if (
        !customField.customField ||
        !nextValue ||
        currentValue === nextValue
      ) {
        return [];
      }

      return [
        {
          dateModified: customField.dateModified,
          id: customField.id,
          nextValue,
        },
      ];
    });

    if (changedFields.length === 0) {
      setOpen(false);
      return;
    }

    await updateCustomFields.mutateAsync({
      matterId: matter.id,
      customFields: changedFields.map(({ dateModified, id, nextValue }) => ({
        dateModified,
        id,
        value: nextValue,
      })),
    });
    toast.success("Mass tort intake details updated successfully");
    setOpen(false);
  };

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title="Update mass tort intake details"
        description="Update the custom field values for this matter."
        isDirty={form.formState.isDirty}
        loading={isFormLoading}
        size="xl"
        footer={
          <div className="flex w-full justify-end gap-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              submitFormId={FORM_ID}
              title="Save"
              loading={isFormLoading}
              disabled={isFormLoading}
            />
          </div>
        }
      >
        <form
          id={FORM_ID}
          className="flex min-h-0 grow flex-col overflow-hidden"
          onSubmit={form.handleSubmit(onSubmit)}
          noValidate
        >
          <div className="overflow-y-auto">
            <fieldset
              disabled={isFormLoading}
              className={slideoverFieldsetClassName}
            >
              {customFields.map((customField, index) => {
                const options = customField.customField?.valueOptions ?? [];
                const isSelectField =
                  customField.customField?.valueType === "select";
                const isDateField =
                  customField.customField?.valueType === "date";
                const fieldName = `customFields.${index}.value` as const;

                return (
                  <FormField
                    key={customField.id || `custom-field-${index}`}
                    control={form.control}
                    name={fieldName}
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <Label>{customField.customField?.label}</Label>
                        <FormControl>
                          {!isSelectField ? (
                            <Input
                              {...field}
                              type={isDateField ? "date" : "text"}
                              placeholder="Enter a value"
                            />
                          ) : (
                            <Select
                              value={field.value}
                              disabled={options.length === 0}
                              onValueChange={field.onChange}
                            >
                              <div className="relative">
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a value" />
                                </SelectTrigger>
                              </div>
                              <SelectContent>
                                {options.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </FormControl>
                        {isSelectField && options.length === 0 ? (
                          <Text variant="subHeading">
                            No options have been configured for this field.
                          </Text>
                        ) : null}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                );
              })}
            </fieldset>
          </div>
        </form>
      </AppModal>
    </Form>
  );
}
